/**
 * Select — custom listbox with a gentle reveal and a check on the chosen option.
 *
 * Ported 1:1 from the Tailwind component library:
 * resources/Dental supply storefront design system/tw-components.jsx
 *
 * Deviations from upstream, all mechanical:
 * • `cx` and the keyframes block are imported rather than defined inline
 *   (upstream is one big file that injects its keyframes at module scope).
 * • `React` is imported explicitly, since upstream relies on a global.
 *
 * Two functional deviations, both for the same reason — `Dialog` clips its
 * body (`overflow-y-auto`) inside a clipping panel (`overflow-hidden`), and
 * an absolutely-positioned menu cannot escape a clipping ancestor at any
 * z-index. Neither `className` nor `style` reaches the menu (both land on
 * the wrapper), so neither could be bent from the call site the way
 * `Pagination` bends the DS pager through `style`:
 * • `menuPlacement="top"` opens the menu upward, anchored to the trigger
 *   rather than the wrapper (the wrapper also holds the label, so `bottom:
 *   100%` would float the menu a label's height clear of the field).
 *   Default `"bottom"` is unchanged. No auto-flip — placement is the
 *   caller's call, deliberately, so nothing here has to measure layout.
 * • `menuMaxHeight` caps the scroll area. Upstream's 260px is taller than
 *   the room above the trigger in a dialog, so the menu's far edge got
 *   cropped; a shorter cap scrolls internally instead. Default is upstream's
 *   `"260px"`.
 * • `menuMinHeight` floors it, so a menu with few (or not-yet-loaded)
 *   options doesn't render as a sliver. Default is unset, i.e. upstream's
 *   content-driven height. An option row is 44px (`--text-base` 16px ×
 *   `--leading-normal` 1.5, plus `py-[10px]`) and the menu adds 12px of its
 *   own `p-[6px]`, so N rows is `N * 44 + 12`.
 *
 * `onChange` receives the **value**, not an event. Every class here resolves to a
 * DS token via the Tailwind preset — don't "fix" one into a literal pixel.
 *
 * Known upstream gaps, deliberately not patched here so this file stays diffable:
 * no Arrow-key navigation, no Escape-to-close, no aria-activedescendant, and it
 * accepts no `...rest` so the trigger can't take an aria-label (wrap it in a
 * labelled `role="group"` if it needs one). See the skill's backlog.
 */
import React from "react";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import "./keyframes";

export function Select({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  style = {},
  className = "",
  menuPlacement = "bottom",
  menuMaxHeight = "260px",
  menuMinHeight,
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const norm = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );
  const selected = norm.find((o) => o.value === value);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div
      className={cx(
        "dnd-select relative flex flex-col gap-[var(--space-2)] font-sans",
        className
      )}
      style={style}
      ref={ref}
    >
      {label && (
        <span className="dnd-select__label text-sm font-semibold text-ink">
          {label}
        </span>
      )}
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          "dnd-select__trigger flex items-center gap-[var(--space-2)] w-full h-[var(--control-height-md)] pl-[var(--space-4)] pr-[var(--space-3)] bg-card border-[1.5px] rounded-[var(--radius-control)] cursor-pointer font-sans text-base text-ink text-left",
          "[transition:border-color_var(--duration-fast),box-shadow_var(--duration-base)_var(--ease-out)] focus-visible:outline-none focus-visible:border-line-strong focus-visible:shadow-md",
          open
            ? "border-line-strong shadow-md"
            : "border-line hover:border-line-strong"
        )}
      >
        <span
          className={cx(
            "dnd-select__value flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
            !selected && "text-ink-3"
          )}
        >
          {selected ? selected.label : placeholder}
        </span>
        <Icon
          className={cx(
            "dnd-select__chev text-ink-3 [transition:transform_var(--duration-base)_var(--ease-spring)]",
            open && "rotate-180"
          )}
          name="expand_more"
          size={20}
          weight={500}
        />
      </button>
      {open && (
        <div
          className={cx(
            "dnd-select__menu absolute left-0 min-w-full w-max max-w-[320px] z-[var(--z-dropdown)] bg-card border border-line-subtle rounded-md shadow-lg p-[6px] overflow-y-auto",
            menuPlacement === "top"
              ? // Anchored to the TRIGGER, not the wrapper. `bottom: 100%`
                // would be the top of `.dnd-select`, which also contains the
                // label — leaving the menu floating a label's height above
                // the field. The trigger is the last flex child at a fixed
                // `--control-height-md`, so measuring that far up from the
                // wrapper's bottom lands 6px above the input, mirroring the
                // downward case exactly.
                "bottom-[calc(var(--control-height-md)+6px)] origin-bottom animate-[dnd-select-in-up_var(--duration-base)_var(--ease-out)]"
              : "top-[calc(100%+6px)] origin-top animate-[dnd-select-in_var(--duration-base)_var(--ease-out)]"
          )}
          style={{ maxHeight: menuMaxHeight, minHeight: menuMinHeight }}
          role="listbox"
        >
          {norm.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              onClick={() => {
                onChange && onChange(o.value);
                setOpen(false);
              }}
              className={cx(
                "dnd-select__opt flex items-center gap-[var(--space-2)] w-full py-[10px] px-[var(--space-3)] rounded-sm border-0 bg-transparent appearance-none text-base cursor-pointer text-left",
                "[transition:background_var(--duration-base)_var(--ease-out),color_var(--duration-fast)_var(--ease-standard)] hover:bg-neutral-100 focus-visible:outline-none focus-visible:bg-neutral-100",
                o.value === value
                  ? "text-orange-700 font-medium"
                  : "text-ink-2 hover:text-ink"
              )}
            >
              <span className="whitespace-nowrap">{o.label}</span>
              {o.value === value && (
                <Icon
                  className="ml-auto pl-[var(--space-2)] text-orange-600"
                  name="check"
                  size={18}
                  weight={600}
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
